import React from 'react';
import Icon from '../../../components/AppIcon';

const PerformanceKPIStrip = ({ dateRange, selectedAccounts }) => {
  // Mock KPI data based on selected filters
  const kpiData = [
    {
      id: 'total-pnl',
      title: 'Total P&L',
      value: 45847.32,
      currency: 'USD',
      change: 12.5,
      changeType: 'positive',
      sparklineData: [100, 120, 115, 140, 135, 155, 145, 165, 160, 180],
      icon: 'TrendingUp',
      description: 'Cumulative profit/loss across all positions'
    },
    {
      id: 'win-rate',
      title: 'Win Rate',
      value: 68.4,
      unit: '%',
      change: 2.1,
      changeType: 'positive',
      sparklineData: [65, 67, 66, 69, 68, 70, 69, 71, 68, 68.4],
      icon: 'Target',
      description: 'Percentage of profitable trades'
    },
    {
      id: 'avg-trade-duration',
      title: 'Avg Trade Duration',
      value: 4.2,
      unit: 'hours',
      change: -0.8,
      changeType: 'negative',
      sparklineData: [5.2, 4.8, 5.1, 4.5, 4.3, 4.7, 4.1, 4.4, 4.0, 4.2],
      icon: 'Clock',
      description: 'Average time positions are held'
    },
    {
      id: 'sharpe-ratio',
      title: 'Sharpe Ratio',
      value: 1.84,
      unit: '',
      change: 0.15,
      changeType: 'positive',
      sparklineData: [1.2, 1.4, 1.3, 1.6, 1.5, 1.7, 1.6, 1.8, 1.7, 1.84],
      icon: 'BarChart3',
      description: 'Risk-adjusted return measure'
    }
  ];

  const formatValue = (value, currency, unit) => {
    if (currency) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
      })?.format(value);
    }
    return `${value}${unit}`;
  };

  const formatChange = (change, changeType) => {
    const sign = change > 0 ? '+' : '';
    const color = changeType === 'positive' ? 'text-green-600' : 'text-red-600';
    return (
      <span className={`${color} text-sm font-medium`}>
        {sign}{change}%
      </span>
    );
  };

  const renderSparkline = (data, changeType) => {
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min;
    
    const points = data?.map((value, index) => {
      const x = (index / (data?.length - 1)) * 100;
      const y = 100 - ((value - min) / range) * 100;
      return `${x},${y}`;
    })?.join(' ');

    const strokeColor = changeType === 'positive' ? '#059669' : '#DC2626';

    return (
      <svg className="w-16 h-8" viewBox="0 0 100 100" preserveAspectRatio="none">
        <polyline
          fill="none"
          stroke={strokeColor}
          strokeWidth="2"
          points={points}
        />
      </svg>
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {kpiData?.map((kpi) => (
        <div
          key={kpi?.id}
          className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-shadow duration-200"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name={kpi?.icon} size={16} className="text-primary" />
              </div>
              <h3 className="text-sm font-medium text-muted-foreground">
                {kpi?.title}
              </h3>
            </div>
            {renderSparkline(kpi?.sparklineData, kpi?.changeType)}
          </div>

          <div className="space-y-2">
            <div className="flex items-baseline space-x-2">
              <span className="text-2xl font-bold text-foreground">
                {formatValue(kpi?.value, kpi?.currency, kpi?.unit)}
              </span>
              {formatChange(kpi?.change, kpi?.changeType)}
            </div>
            
            <p className="text-xs text-muted-foreground">
              {kpi?.description}
            </p>
          </div>

          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Period: {dateRange}</span>
              <span>{selectedAccounts?.length} account(s)</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PerformanceKPIStrip;